import type { QueryClient } from "@tanstack/react-query";

import { removeTrigger, replaceTrigger } from "../triggerList";
import type { TriggerRecord } from "../TriggerRecord";

import { triggersQueryKeys } from "./triggersQueryKeys";

/**
 * Writes a trigger the server returned to its by-id query and, in place of
 * its old copy, to its own org's cached list, then refetches both. The list's
 * copy is found by id, so a rename replaces it like any other update. A list
 * that isn't cached is left alone, and a cached list with no old copy (as
 * after a create) shows the trigger only once its refetch lands.
 *
 * The refetch cancels a fetch already in flight without rolling it back, so
 * neither that fetch nor a later rollback (as on unmount) can undo the write.
 */
export function storeTrigger(
  queryClient: QueryClient,
  record: TriggerRecord,
): Promise<unknown> {
  const byId = triggersQueryKeys.byId(record.trigger_id);
  const list = triggersQueryKeys.list(record.org_id);
  queryClient.setQueryData(byId, record);
  queryClient.setQueryData<readonly TriggerRecord[]>(list, (cached) =>
    cached === undefined ? undefined : replaceTrigger(cached, record),
  );
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: byId }),
    queryClient.invalidateQueries({ queryKey: list }),
  ]);
}

/**
 * Removes a deleted trigger from its by-id query and from its own org's
 * cached list, then refetches the list. A list that isn't cached is left
 * alone.
 */
export function forgetTrigger(
  queryClient: QueryClient,
  trigger: Pick<TriggerRecord, "org_id" | "trigger_id">,
): Promise<void> {
  const list = triggersQueryKeys.list(trigger.org_id);
  queryClient.removeQueries({
    queryKey: triggersQueryKeys.byId(trigger.trigger_id),
  });
  queryClient.setQueryData<readonly TriggerRecord[]>(list, (cached) =>
    cached === undefined
      ? undefined
      : removeTrigger(cached, trigger.trigger_id),
  );
  return queryClient.invalidateQueries({ queryKey: list });
}
